import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface MessageHistoryProps {
  subscriberId: string;
  showQuoteDetails?: boolean;
}

interface MessageLog {
  id: string;
  quote_text: string;
  status: string;
  sent_at: string;
  category_names?: string[];
}

const MessageHistory = ({ subscriberId, showQuoteDetails = false }: MessageHistoryProps) => {
  const [messages, setMessages] = useState<MessageLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const [selectedQuote, setSelectedQuote] = useState<MessageLog | null>(null);

  useEffect(() => {
    const fetchMessageHistory = async () => {
      if (!subscriberId) return;
      
      setIsLoading(true);
      try {
        let query = supabase
          .from('message_logs')
          .select('id, quote_text, status, sent_at, category_names')
          .eq('profile_id', subscriberId)
          .order('sent_at', { ascending: false });
          
        if (dateRange?.from) {
          query = query.gte('sent_at', dateRange.from.toISOString());
        }
        
        if (dateRange?.to) {
          const endDate = new Date(dateRange.to);
          endDate.setDate(endDate.getDate() + 1);
          query = query.lt('sent_at', endDate.toISOString());
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        setMessages(data || []);
      } catch (error) {
        console.error('Error fetching message history:', error);
        toast({
          title: "Error",
          description: "Failed to load message history",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMessageHistory();
  }, [subscriberId, dateRange]);
  
  const clearDateFilter = () => {
    setDateRange(undefined);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Message History</h3>
        
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="gap-2"
              >
                <CalendarIcon className="h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(dateRange.from, "LLL dd, y")
                  )
                ) : (
                  "Filter by date"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={1}
              />
              <div className="p-3 border-t border-border">
                <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                  Clear filter
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      {isLoading ? (
        <div className="text-center py-8">Loading message history...</div>
      ) : messages.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No message history found
          {dateRange?.from && " for the selected date range"}
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent At</TableHead>
                <TableHead>Quote</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((message) => (
                <TableRow 
                  key={message.id} 
                  className={showQuoteDetails ? "cursor-pointer hover:bg-muted/50" : ""}
                  onClick={() => showQuoteDetails && setSelectedQuote(message)}
                >
                  <TableCell className="whitespace-nowrap">
                    {new Date(message.sent_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-md truncate">{message.quote_text}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {message.category_names?.map((category, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={message.status === 'sent' ? 'default' : 'destructive'}>
                      {message.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      {showQuoteDetails && selectedQuote && (
        <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
          <DialogContent className="sm:max-w-[90vw] md:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Quote Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-1">Quote</h4>
                <p className="text-sm whitespace-pre-wrap break-words">{selectedQuote.quote_text}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">Categories</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedQuote.category_names?.map((category, index) => (
                    <Badge key={index} variant="outline">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">Sent At</h4>
                <p className="text-sm">{new Date(selectedQuote.sent_at).toLocaleString()}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1">Status</h4>
                <Badge variant={selectedQuote.status === 'sent' ? 'default' : 'destructive'}>
                  {selectedQuote.status}
                </Badge>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MessageHistory;
